// React
import { useMemo } from "react";

// MUI
import { Fade, Tooltip, Typography } from "@mui/material";
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridActionsCellItem, GridColDef, getGridNumericOperators } from "@mui/x-data-grid-pro";

// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import { TypographyCell } from "@Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyHeader } from "@Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { SelectionAndFilterCheckbox } from "../SelectionAndFilterCheckbox";
import { LoadingSpinner } from "@/Layouts/components/LoadingSpinner";

// Helpers
import { getFormatDate } from "@Common/helpers/Dates";
import { getCellClassNameFn } from "@Common/helpers/Tables";

// Icons
import { ReactComponent as ImagePlaceholderIcon } from "@/assets/icons/common/image-placeholder-icon.svg";

// Types
import { GridCellParams } from "@mui/x-data-grid-premium";
import { KMS } from "@DataPages/types/inventory/models/kms";

// Constants
import { InventoryTab } from "../constants";
import { SelectionAndFilterCheckboxAll } from "../SelectionAndFilterAllCheckbox";
import { DATE_FORMAT } from "@/Constants/date";

type KeyActivityManagementTableProps = {
	isLoading: boolean;
	onClickImage: (row: KMS) => void;
	rows: KMS[];
	hasNextPage: boolean;
};

// Constants
const HEADER_CLASS_NAME = "table-header-top-aligned";
const AUTO_GENERATED_ID = "auto-generated-group-footer-root";
const capitalizeString = (str: string) => {
	return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

const minuteOperators = getGridNumericOperators().map((operator) => ({
	...operator,
	InputComponentProps: {
		...operator.InputComponentProps,
		// add an end‐adornment so it reads “4 minutes”
		InputProps: {
			...operator.InputComponentProps?.InputProps,
			endAdornment: "(minutes)",
		},
	},
}));

export default function KeyActivityManagementTable(props: Readonly<KeyActivityManagementTableProps>) {
	const footerModels = {};

	const columns: GridColDef[] = useMemo(() => {
		return [
			{
				field: GRID_CHECKBOX_SELECTION_COL_DEF.field,
				width: 50,
				filterable: false,
				sortable: false,
				resizable: false,
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: () => (
					<SelectionAndFilterCheckboxAll
						className="!p-0"
						table={InventoryTab.KMS}
						data={props.rows}
						origin={InventoryTab.KMS}
						pathToVinNumber="vinNumber"
					/>
				),
				renderCell: (params) => (
					<SelectionAndFilterCheckbox
						_id={params.row._id as string}
						table={InventoryTab.KMS}
						row={params.row as KMS}
						origin={InventoryTab.KMS}
						pathToVinNumber="vinNumber"
						className="!ml-[2px]"
					/>
				),
			},
			{ field: "_id", headerName: "ID", type: "string" },
			{
				field: "image",
				headerName: "Image",
				maxWidth: 90,
				type: "custom",
				headerClassName: HEADER_CLASS_NAME,
				cellClassName: getCellClassNameFn([], {}, {}),
				renderHeader: (params) => <TypographyHeader params={params} />,
				renderCell: (params) => {
					return (
						<GridActionsCellItem
							icon={<ImagePlaceholderIcon />}
							label="Key"
							className="textPrimary"
							color="inherit"
							onClick={() => props.onClickImage(params.row as KMS)}
						/>
					);
				},
			},
			{
				field: "vinNumber",
				headerName: "VIN",
				flex: 1,
				headerClassName: HEADER_CLASS_NAME,
				align: "left",
				width: 150,
				cellClassName: getCellClassNameFn([], {}, {}),
				renderHeader: (params) => <TypographyHeader params={params} />,
				renderCell: (params) => <TypographyCell params={params} />,
			},
			{
				field: "stockId",
				headerName: "Stock #",
				flex: 1,
				headerClassName: HEADER_CLASS_NAME,
				cellClassName: getCellClassNameFn([], {}, {}),
				renderHeader: (params) => <TypographyHeader params={params} />,
				renderCell: (params) => <TypographyCell params={params} />,
			},
			{
				field: "creationDate",
				headerName: "Started",
				flex: 1,
				type: "date",
				headerClassName: HEADER_CLASS_NAME,
				cellClassName: getCellClassNameFn([], {}, {}),
				renderHeader: (params) => <TypographyHeader params={params} />,
				valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.isoTimestampWithZone),
				renderCell: (params) => <TypographyCell params={params} />,
			},
			{
				field: "timeOut",
				headerName: "Time Out",
				type: "number",
				flex: 1,
				renderHeader: (params) => <TypographyHeader params={params} />,
				headerClassName: HEADER_CLASS_NAME,
				cellClassName: getCellClassNameFn([], {}, {}),
				valueGetter: (val) => val,
				valueFormatter: (value) => {
					const totalMinutes = value;
					if (!totalMinutes) {
						return "--";
					}
					const hours = Math.floor(totalMinutes / 60);
					const minutes = totalMinutes % 60;
					return `${hours}hrs ${minutes}m`;
				},
				renderCell: (params) => {
					const totalMinutes = params.row.pastDue;
					if (!totalMinutes) {
						return "--";
					}
					const hours = Math.floor(totalMinutes / 60);
					const minutes = totalMinutes % 60;

					const displayPastDueValue = `${hours}hrs ${minutes}m`;
					const timeOutHours = Math.floor(params?.value / 60);
					const timeOutMinutes = params?.value % 60;
					if (!params.row.pastDue) {
						return <TypographyCell params={params} />;
					}
					return (
						<Tooltip
							title={
								<Typography className="!font-sans !text-xs !font-medium" component="span">
									Past due {displayPastDueValue}
								</Typography>
							}
							placement={"bottom"}
							arrow
						>
							<Typography component={"span"} className="!font-sans !text-xs !font-medium !text-alert">
								{`${timeOutHours}hrs ${timeOutMinutes}m`}
							</Typography>
						</Tooltip>
					);
				},
				filterOperators: minuteOperators,
			},
			{
				field: "createdBy",
				headerName: "Created By", //Name as saved in the provider
				flex: 1,
				type: "string",
				cellClassName: getCellClassNameFn([], {}, {}),
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} />,
				renderCell: (params) => {
					return <TypographyCell params={params}>{capitalizeString(params.value)}</TypographyCell>;
				},
			},
			{
				field: "eventType",
				headerName: "Type",
				type: "string",
				flex: 1,
				headerClassName: HEADER_CLASS_NAME,
				cellClassName: getCellClassNameFn([], {}, {}),
				renderHeader: (params) => <TypographyHeader params={params} />,
				renderCell: (params) => {
					return <TypographyCell params={params}>{capitalizeString(params.value)}</TypographyCell>;
				},
			},
			{
				field: "reason",
				headerName: "Reason",
				type: "string",
				flex: 1,
				cellClassName: getCellClassNameFn([], {}, {}),
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} />,
				renderCell: (params) => <TypographyCell params={params} />,
			},
			{
				field: "kioskId",
				headerName: "Kiosk ID", // Kiosk ID
				type: "string",
				headerClassName: HEADER_CLASS_NAME,
				flex: 1,
				cellClassName: getCellClassNameFn([], {}, {}),
				renderHeader: (params) => <TypographyHeader params={params} />,
				renderCell: (params) => <TypographyCell params={params} />,
			},
			{
				field: "assetId",
				headerName: "Asset ID",
				flex: 1,
				type: "string",
				headerClassName: HEADER_CLASS_NAME,
				cellClassName: getCellClassNameFn([], {}, {}),
				renderHeader: (params) => <TypographyHeader params={params} />,
				renderCell: (params) => <TypographyCell params={params} />,
			},
			{
				field: "transactionId",
				headerName: "Transaction ID",
				flex: 1,
				headerClassName: HEADER_CLASS_NAME,
				type: "string",
				cellClassName: getCellClassNameFn([], {}, {}),
				renderHeader: (params) => <TypographyHeader params={params} />,
				renderCell: (params) => <TypographyCell params={params} />,
			},
		];
	}, [props.onClickImage, props.rows]);

	return (
		<DataSmartTable
			rows={props.rows}
			headerHeight={40}
			rowHeight={25}
			maxRows={20}
			columns={columns}
			disableRowSelectionOnClick
			disableColumnResize
			disableColumnMenu
			checkboxSelection
			columnVisibilityModel={{ _id: false }}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					customComponent: (
						<Fade in={props.hasNextPage} timeout={400}>
							<div>
								<LoadingSpinner label="Key Management Service data is still loading..." className="items-center" />
							</div>
						</Fade>
					),
					filter: {
						hide: false,
						disabled: false,
					},
					search: {
						data: props.rows || [],
						hide: false,
						disabled: false,
					},
					titleExpandible: {
						className: "mb-5",
						hide: true,
					},
				},
				noRowsOverlay: {
					title: "No Key Management Service",
				},
			}}
			getCellClassName={(params: GridCellParams) => {
				// Targeting footer
				if (params.id === AUTO_GENERATED_ID) {
					// Use this to change the cell class name depending on the cell value
					const negatives = Object.keys(footerModels);
					if (negatives.includes(params.field) && (params.value as number) < 0) {
						return "aggregation-footer-negative";
					}

					// Append label 'Total' to the cell value in footer
					if (params.field === "saleDate") {
						return "aggregation-footer-total";
					}
				}

				return "";
			}}
			customProps={{
				tableContainerStyle: { height: "auto", width: "100%" },
				tableClassName: {
					"functional-table footer-table-included": true,
					"display-select-all-checkbox": true,
					"pt-6": true,
				},
				aggregationFooterClassName: "aggregation-footer regular-footer",
				expandableRows: false,
				hide: false,
			}}
		/>
	);
}
